import classNames from "classnames";
import React, { CSSProperties, FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Overlay } from "../overlay/overlay";
import { OverlayProps } from "../overlay/overlay.types";
import { CSS_CLASSES } from "./drawer-css-classes";

export interface DrawerSlotRendererProps {
  open: boolean;
}

export type DrawerSlotRenderer = (props: DrawerSlotRendererProps) => JSX.Element;

export type DrawerExtension = "fit-content" | "full";

export type DrawerToOverlayPassthroughProps = Pick<
  OverlayProps,
  "closeOnEscapeKey" | "closeOnOutsideClick" | "showBackdrop" | "makeOthersInert"
>;

export interface DrawerOwnProps {
  /**
   * A unique id to be applied to the root DOM element.
   */
  id?: string;

  /**
   * Slides the drawer open or close.
   * Only reveals the `slidingContent`.
   * The `fixedContent` (if any) is always visible.
   */
  open: boolean;

  /**
   * This event is triggered when the Drawer closes either on `Esc` key press or when the user clicks outside the Drawer
   * content area.
   *
   * A handler function can be attached to this event to allow the component user to manage the `open` state, thus
   * allowing this component to function as a controlled component.
   */
  onClose?: () => void;

  /**
   * Which side of the screen the drawer slides in from.
   *
   * @default left
   */
  slideFrom?: "left" | "bottom" | "right";

  /**
   * Specifies to what extent the drawer opens up.
   *
   * @default fit-content
   */
  extension?: DrawerExtension;

  /**
   * Content to be rendered as part of the drawer that will be hidden and revealed
   * when the drawer closes or opens respectively.
   */
  slidingContent?: DrawerSlotRenderer | JSX.Element;

  /**
   * Content to the rendered as part of the drawer that will always be visible,
   * whether the drawer is open or closed.
   */
  fixedContent?: DrawerSlotRenderer | JSX.Element;

  /**
   * This prop controls if a drawer handle element is rendered.
   * The prop accepts a drawer element or a render function that is passed the `open` state of the drawer.
   * The handle element must accept a ref.
   *
   * If present, it is always visible and slides along with the `slidingContent`.
   */
  handle?: DrawerSlotRenderer | JSX.Element;

  /**
   * A callback function that's called when the drawer offset for the `fixedContent` changes.
   * This can be helpful for the parent component as it can use this offset value to
   * set/adjust any margin/padding for its content that the drawer is rendered on
   * top of. This way the parent's content can avoid getting hidden behind the drawer.
   */
  onOffsetChange?: (offset: number) => void;

  /**
   * Apply/override styling by passing in custom CSS class-name(s) using this prop.
   */
  className?: string;

  /**
   * Class name applied to a container element that contains both the sliding and the fixed content.
   * Use this to give the drawer content as a whole some common styling, like a background color.
   */
  contentContainerClassName?: string;

  /**
   * Class name applied to a container element that contains the sliding content.
   */
  slidingContentContainerClassName?: string;

  /**
   * Class name applied to a container element that contains the fixed content.
   */
  fixedContentContainerClassName?: string;

  /**
   * Class name applied to a container element that contains the drawer handle.
   */
  handleContainerClassName?: string;
}

export type DrawerProps = DrawerOwnProps & DrawerToOverlayPassthroughProps;

export const Drawer: FC<DrawerProps> = memo(
  ({
    id,
    open,
    onClose,
    slideFrom = "left",
    extension = "fit-content",
    slidingContent,
    fixedContent,
    handle,
    onOffsetChange,
    className,
    contentContainerClassName,
    slidingContentContainerClassName,
    fixedContentContainerClassName,
    handleContainerClassName,
    // overlay passthrough props
    closeOnEscapeKey,
    closeOnOutsideClick,
    showBackdrop,
    makeOthersInert,
    // rest props
    ...restProps
  }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [closeDrawer, setCloseDrawer] = useState(true);

    const drawerRef = useRef<HTMLDivElement>(null);

    const getOffset = useCallback((node, slideFrom) => {
      const { height, width } = node.getBoundingClientRect();
      return slideFrom === "bottom" ? height : width;
    }, []);

    const slidingContentJSX =
      (slidingContent && typeof slidingContent === "function"
        ? slidingContent({ open: drawerOpen })
        : slidingContent) ?? null;

    const fixedContentJSX =
      (fixedContent && typeof fixedContent === "function" ? fixedContent({ open: drawerOpen }) : fixedContent) ?? null;

    // Maintain the fixed content offset in state
    const [fixedContentOffset, setFixedContentOffset] = useState(0);
    const fixedContentRefCallback = useCallback(
      (node: HTMLDivElement) => {
        if (fixedContentJSX && node !== null) {
          setFixedContentOffset(getOffset(node, slideFrom));
        } else {
          setFixedContentOffset(0);
        }
      },
      [fixedContentJSX, getOffset, slideFrom]
    );

    const handleJSX = (handle && typeof handle === "function" ? handle({ open: drawerOpen }) : handle) ?? null;

    // Maintain the handle offset as state
    const [handleOffset, setHandleOffset] = useState(0);
    const handleRefCallback = useCallback(
      (node: HTMLDivElement) => {
        if (handleJSX && node !== null) {
          setHandleOffset(getOffset(node, slideFrom));
        } else {
          setHandleOffset(0);
        }
      },
      [getOffset, handleJSX, slideFrom]
    );

    const [offset, setOffset] = useState(0);
    useEffect(() => {
      let newOffset = 0;
      if (handle || fixedContent) {
        newOffset = handleOffset + fixedContentOffset;
        if (newOffset !== offset) {
          setOffset(newOffset);
          onOffsetChange?.(newOffset);
        }
      }
    }, [fixedContent, fixedContentOffset, handle, handleOffset, offset, onOffsetChange]);

    const translateRuleWhenOpen = useMemo(() => {
      switch (slideFrom) {
        case "left":
          return "translateX(0)";
        case "bottom":
          return "translateY(0)";
        case "right":
          return "translateX(0)";
      }
    }, [slideFrom]);
    const translateRuleWhenClosed = useMemo(() => {
      switch (slideFrom) {
        case "left":
          return `translateX(calc(-100% + ${offset}px))`;
        case "bottom":
          return `translateY(calc(100% - ${offset}px))`;
        case "right":
          return `translateX(calc(100% - ${offset}px))`;
      }
    }, [offset, slideFrom]);

    const drawerStyle = useMemo(() => {
      const style: CSSProperties = {};
      if (closeDrawer) {
        style.transform = translateRuleWhenClosed;
      } else if (drawerOpen) {
        style.transform = translateRuleWhenOpen;
      }
      return style;
    }, [closeDrawer, drawerOpen, translateRuleWhenClosed, translateRuleWhenOpen]);

    const drawerClassName = useMemo(
      () =>
        classNames(
          CSS_CLASSES.DRAWER_ROOT,
          {
            [CSS_CLASSES.DRAWER_OPEN]: open,
            [CSS_CLASSES.DRAWER_VISIBLE]: open || fixedContent || handle,
            [CSS_CLASSES.DRAWER_SLIDE_FROM_LEFT]: slideFrom === "left",
            [CSS_CLASSES.DRAWER_SLIDE_FROM_BOTTOM]: slideFrom === "bottom",
            [CSS_CLASSES.DRAWER_SLIDE_FROM_RIGHT]: slideFrom === "right",
            [CSS_CLASSES.DRAWER_EXTENSION_FULL]: extension === "full",
          },
          className
        ),
      [open, fixedContent, handle, slideFrom, extension, className]
    );

    const contentClassName = useMemo(
      () =>
        classNames(CSS_CLASSES.DRAWER_CONTENT_CONTAINER, {
          [CSS_CLASSES.DRAWER_SLIDE_FROM_LEFT_CONTENT_CONTAINER]: slideFrom === "left",
          [CSS_CLASSES.DRAWER_SLIDE_FROM_BOTTOM_CONTENT_CONTAINER]: slideFrom === "bottom",
          [CSS_CLASSES.DRAWER_SLIDE_FROM_RIGHT_CONTENT_CONTAINER]: slideFrom === "right",
          [contentContainerClassName]: drawerOpen || fixedContent,
        }),
      [slideFrom, contentContainerClassName, drawerOpen, fixedContent]
    );

    const slidingContentClassName = useMemo(
      () =>
        classNames(CSS_CLASSES.DRAWER_SLIDING_CONTENT_CONTAINER, { [slidingContentContainerClassName]: drawerOpen }),
      [drawerOpen, slidingContentContainerClassName]
    );

    const fixedContentClassName = useMemo(
      () => classNames(CSS_CLASSES.DRAWER_FIXED_CONTENT_CONTAINER, fixedContentContainerClassName),
      [fixedContentContainerClassName]
    );

    const handleClassName = useMemo(
      () => classNames(CSS_CLASSES.DRAWER_HANDLE_CONTAINER, handleContainerClassName),
      [handleContainerClassName]
    );

    const handleTransitionEnd = useCallback(
      (event: React.TransitionEvent<HTMLDivElement>) => {
        if (event.target === drawerRef.current) {
          if (closeDrawer) {
            setDrawerOpen(false);
            onClose?.();
          }
          if (drawerOpen) {
            drawerRef.current.focus();
          }
        }
      },
      [closeDrawer, drawerOpen, onClose]
    );

    const startClosingDrawer = useCallback(() => {
      setCloseDrawer(true);
    }, []);

    useEffect(() => {
      if (open) {
        setDrawerOpen(true);
        setCloseDrawer(false);
      }
    }, [open]);

    useEffect(() => {
      if (!open) {
        startClosingDrawer();
      }
    }, [open, startClosingDrawer]);

    return (
      <Overlay
        open={drawerOpen}
        onClose={onClose}
        lazyPortal={false}
        alwaysRenderContent={true}
        clipContainer={true}
        closeOnEscapeKey={closeOnEscapeKey}
        closeOnOutsideClick={closeOnOutsideClick}
        showBackdrop={showBackdrop}
        makeOthersInert={makeOthersInert}
      >
        <div
          id={id}
          ref={drawerRef}
          onTransitionEnd={handleTransitionEnd}
          className={drawerClassName}
          style={drawerStyle}
          tabIndex={0}
          {...restProps}
        >
          <div className={contentClassName}>
            {slidingContentJSX && <div className={slidingContentClassName}>{slidingContentJSX}</div>}
            {fixedContentJSX && (
              <div className={fixedContentClassName} ref={fixedContentRefCallback}>
                {fixedContentJSX}
              </div>
            )}
          </div>
          {handleJSX && (
            <div className={handleClassName} ref={handleRefCallback}>
              {handleJSX}
            </div>
          )}
        </div>
      </Overlay>
    );
  }
);
Drawer.displayName = "Drawer";
